import { Component, ElementRef, ViewChild, AfterViewInit, OnDestroy, ViewEncapsulation, Input, OnChanges, SimpleChanges, Optional, OnInit, inject, signal, computed, Renderer2, effect } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { fromEvent, Subject, forkJoin, of, combineLatest } from 'rxjs';
import { debounceTime, takeUntil, finalize, catchError, switchMap, filter, take, map } from 'rxjs/operators';
import { MatDatepicker, MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatNativeDateModule } from '@angular/material/core';
import { NgSelectModule } from '@ng-select/ng-select';
import { RouterLink } from '@angular/router';
import { QuillModule } from 'ngx-quill';
import Quill from 'quill';
import SnowTheme from 'quill/themes/snow';
import * as Highcharts from 'highcharts';

// Initialize Highcharts modules with better error handling
let modulesLoaded = false;

const loadHighchartsModules = async (): Promise<boolean> => {
  if (modulesLoaded) return true;

  try {
    // Load highcharts-more first
    const HighchartsMore = await import('highcharts/highcharts-more');
    if (HighchartsMore.default && typeof HighchartsMore.default === 'function') {
      HighchartsMore.default(Highcharts);
    }

    // Load solid-gauge module
    const SolidGauge = await import('highcharts/modules/solid-gauge');
    if (SolidGauge.default && typeof SolidGauge.default === 'function') {
      SolidGauge.default(Highcharts);
    }

    // Verify modules are loaded
    if ((Highcharts as any).seriesTypes?.solidgauge) {
      modulesLoaded = true;
      return true;
    } else {
      console.warn('Modules loaded but solidgauge not available');
      return false;
    }
  } catch (error) {
    console.warn('Failed to load Highcharts modules:', error);
    return false;
  }
};

// Preload modules
loadHighchartsModules();

// Register Quill Snow theme for Quill 2.x
// This must happen before any Quill instances are created
// Register the theme so ngx-quill can find it when it tries to import
Quill.register('themes/snow', SnowTheme, true);

import { ProjectStatusService } from './project-status.service';
import { ApiService } from '@app/core/services/API/api-service';
import { NoteService, ProjectsService } from '../projects/projects.service';
import { Spinner } from '../../shared/components/spinner/spinner';
import { DialogService } from '@app/shared/services/dialog.service';
import { ToastrService } from 'ngx-toastr';
import { ProjectCompletionModal } from '@app/shared/components/project-completion-modal/project-completion-modal';
import { ConfirmPopup } from '@app/shared/components/confirm-popup/confirm-popup';
import { CrossTabSyncService, CrossTabSyncEvent } from '@app/core/services/cross-tab-sync/cross-tab-sync.service';

// Function to check if Highcharts modules are loaded
function areHighchartsModulesLoaded(): boolean {
  return !!(Highcharts as any).seriesTypes?.solidgauge;
}

export interface NoteDocument {
  name: string;
  type: string;
  url: string;
}

export interface Note {
  id: string;
  text: string;
  type: 'text' | 'image' | 'video' | 'audio' | 'document';
  user: string;
  date: string;
  createdAt: string; // Add raw timestamp for sorting
  isPinned: boolean;
  fileUrl?: string; // Deprecated: use documents array instead
  fileName?: string; // Deprecated: use documents array instead
  fileType?: string; // Deprecated: use documents array instead
  documents?: NoteDocument[]; // Array of all documents attached to the note
  time?: string;
}

export interface ApiNoteResponse {
  _id: string;
  status: string;
  textHTML: string;
  objCode: string;
  objID: string;
  projectID: string;
  document?: Array<{
    name: string;
    type: string;
    url: string;
  }>;
  createdBy: {
    _id: string;
    userID: string;
    name: string;
    email: string;
    utype: string;
    accesslevelID: string;
    accesslevelType: string;
    accesslevel_name: string;
    homeTeamID: string;
    agencyID: string;
    agencyDbID: string;
    en_id: string;
    jobInfo: string;
    image: string;
    timezone: string;
    managerID: string;
    accessor: string;
  };
  url?: string;
  fileType?: string;
  createdAt: string;
  updatedAt: string;
  __v: number;
}

export interface ApiNotesResponse {
  success: boolean;
  message: string;
  data: ApiNoteResponse[];
}

export interface ProjectStatusData {
  projectCompletion: number;
  plannedHours: number;
  filledHours: number;
  unfilledHours: number;
  offsetHours: number;
  ProjectPercentage?: number;

}

export interface ProjectHourData {
  billable: {
    total: number;
    task: number;
    project: number;
    request: number;
    revisions: number;
  },
  nonBillable: {
    total: number;
    issue: number;
  }

}

export interface ChartConfig {
  height: number;
  width: number;
  backgroundColor: string;
  colors: {
    primary: string;
    secondary: string;
    background: string;
  };
}

@Component({
  selector: 'app-project-status',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatInputModule,
    MatNativeDateModule,
    FormsModule,
    NgSelectModule,
    QuillModule,
    Spinner,
    ProjectCompletionModal,
    ConfirmPopup
  ],
  templateUrl: './project-status.html',
  styleUrls: ['./project-status.scss'],
  encapsulation: ViewEncapsulation.None as const,
})
export class ProjectStatusComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges {
  // ViewChild references
  @ViewChild('projectStatusChartContainer') projectStatusChartContainer!: ElementRef<HTMLDivElement>;
  @ViewChild('balanceHoursChartContainer') balanceHoursChartContainer!: ElementRef<HTMLDivElement>;
  @ViewChild('datepicker') datepicker!: MatDatepicker<Date>;
  @ViewChild('hintIconWrapper', { static: false }) hintIconWrapperRef!: ElementRef;
  @ViewChild('goalDateTooltip', { static: false }) goalDateTooltipRef!: ElementRef;

  // Input properties for JSON data integration
  @Input() projectData: ProjectStatusData = {
    projectCompletion: 10,
    plannedHours: 80,
    filledHours: 39.76,
    unfilledHours: -12.59,
    offsetHours: 9.5
  };

    // data from getProjectHourData API
    projectHourData = signal<any>(null);
    projectHourStatus = signal<any>(null);
    projectsPlannedDateLog = signal<any>(null);
    projectPercentage: number = 0;
    currentProjectId: string | null = null;
  // Component state
  private chartInstances: Highcharts.Chart[] = [];
  private projectStatusChart: Highcharts.Chart | null = null;
  private balanceHoursChart: Highcharts.Chart | null = null;
    private projectSerivce = inject(ProjectsService);
    /** Tracks current project id reactively so refresh effect runs when id becomes available */
    private readonly currentProjectIdSignal = toSignal(this.projectSerivce.currentProjectId$, {
      initialValue: null as string | null,
    });
    private noteService = inject(NoteService);
  private crossTabSyncService = inject(CrossTabSyncService);
  
  // Consolidated timeout management
  private activeTimeouts: Set<any> = new Set();
  private loadingTimeout: any = null;
  private fallbackTimeout: any = null;
  private retryTimeout: any = null;
  private updateTimeout: any = null;
  selectedDate: Date = new Date();
  selectedGoalDate: Date = new Date();
  isCalendarOpen: boolean = false;
  selectedTab: string = 'hours';
  isTabContentVisible: boolean = true;
  isLoading: boolean = false;
  hasError: boolean = false;
  isChartsLoading: boolean = true;
  responsibilityOptions = [
    { name: 'TW - INTL' },
    { name: 'TW - CAN' },
    { name: 'Timeline Correction'},
    { name: 'Client'}
  ];
  selectedResponsibility : string = 'none';
  // Chart configuration
  private readonly chartConfig: ChartConfig = {
    height: 260,
    width: 220,
    backgroundColor: 'transparent',
    colors: {
      primary: '#F46D73',
      secondary: '#f26666',
      background: '#A7BBCE'
    }
  };

  showFilterModal = false;
  activeFilterCount = signal(0);
  activeFilterNames = signal<string[]>([]);
  currentFilterData = signal<any>(null);

  // Project completion modal
  showProjectCompletionSlider = signal(false);
  projectCompletionModalData = signal<any>(null);

  // Export modal
  showExportModal = false;

  showGoalDateTooltip = false;
  goalDateTooltipRows = signal<any[]>([]);

  hideTooltipTimeout: any = null;
  private tooltipPositionRetryCount = 0;
  private readonly tooltipPositionMaxRetries = 5;
  tooltipPositioned = false;

  constructor(private dialogService: DialogService, private toastrService: ToastrService, @Optional() private projectStatusService?: ProjectStatusService) {
    // Ensure Quill theme is registered before any editor instances are created
    try {
      const theme = Quill.import('themes/snow');
      if (!theme) {
        Quill.register('themes/snow', SnowTheme, true);
      }
    } catch (e) {
      // Theme not found, register it
      Quill.register('themes/snow', SnowTheme, true);
    }
    
    this.projectSerivce.currentProjectId$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(projectId => {
      if (projectId) {
        const previousProjectId = this.currentProjectId;
        this.currentProjectId = projectId;

        // If project changed, reload data and align refresh cursor so the refresh effect
        // does not immediately duplicate-load when global refreshCount > 0 from prior actions
        if (previousProjectId !== projectId) {
          this.lastRefreshCount = this.projectSerivce.projectDataRefreshTrigger$();
          this.loadAllProjectData(projectId);
        }
      } else {
        this.currentProjectId = null;
      }
    });

    this.projectSerivce.projectPercentage$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(projectPercentage => {
      if (projectPercentage !== undefined && projectPercentage !== null) {
        const newPercentage = typeof projectPercentage === 'string' ? Number(projectPercentage) : projectPercentage;
        // Only update if percentage actually changed
        if (this.projectPercentage !== newPercentage) {
          this.projectPercentage = newPercentage;
          // Trigger chart update when percentage changes
          if (this.selectedTab === 'hours') {
            // Try to update existing chart first, recreate if needed
            if (this.projectStatusChart) {
              try {
                this.updateProjectStatusChart(newPercentage);
              } catch (error) {
                console.warn('Failed to update project status chart, recreating:', error);
                this.destroyCharts();
                const timeout = this.trackTimeout(setTimeout(() => {
                  this.checkAndInitializeCharts();
                }, 100));
              }
            } else {
              // Chart doesn't exist, initialize it
              const timeout = this.trackTimeout(setTimeout(() => {
                this.checkAndInitializeCharts();
              }, 100));
            }
          }
        }
      }
    });

    // Watch for project data refresh trigger using effect
    // Must read refresh signal AND project id signal so the effect re-runs when either changes
    // (plain currentProjectId property is not reactive — early return used to skip subscribing to refresh)
    effect(() => {
      const refreshCount = this.projectSerivce.projectDataRefreshTrigger$();
      const projectId = this.currentProjectIdSignal();
      if (!projectId || refreshCount <= 0 || refreshCount === this.lastRefreshCount) {
        return;
      }
      this.lastRefreshCount = refreshCount;
      setTimeout(() => {
        const id = this.currentProjectIdSignal();
        if (id) {
          this.loadAllProjectData(id);
        }
      }, 50);
    });

    // Watch for changes in projectHourStatus signal to update charts
    // Only update if charts already exist (to avoid infinite loops during initial load)
    effect(() => {
      // Only run after component is initialized to avoid running during construction
      if (!this.isComponentInitialized) return;

      const hourStatus = this.projectHourStatus();
      if (hourStatus && this.selectedTab === 'hours' && this.currentProjectId &&
          this.chartInstances.length > 0) {
        // Create a hash of the hour status to detect actual value changes
        const currentHash = `${hourStatus.unfilledHours}_${hourStatus.totalPlannedHours}_${hourStatus.filledHours || 0}`;

        // Only update if values actually changed
        if (currentHash !== this.lastHourStatusHash) {
          this.lastHourStatusHash = currentHash;
          // When hour status changes and charts exist, update them with fresh data
          // Use a small delay to ensure signal has fully updated
          setTimeout(() => {
            if (this.projectStatusChartContainer?.nativeElement &&
                this.balanceHoursChartContainer?.nativeElement) {
              this.updateChartsWithFreshData();
            }
          }, 150);
        }
      }
    });

    // Setup cross-tab synchronization for chart updates
    this.setupCrossTabSync();
  }


  private isComponentInitialized = false; // Flag to prevent effects from running during initial construction

  ngOnInit(): void {
    // Set initial loading state to true for hours tab
    this.isChartsLoading = true;
    // Mark component as initialized immediately in ngOnInit
    // This ensures effects can run when data changes in the same tab
    this.isComponentInitialized = true;
  }

  loadAllProjectData(projectId: string): void {
    this.isChartsLoading = true; // Show spinner when starting to load data

    // Use forkJoin to wait for all API calls to complete
    // Also fetch project details to get updated percentage when status changes
    forkJoin({
      hourData: this.projectSerivce.getProjectHourData(projectId),
      hourStatus: this.projectSerivce.getProjectHourStatus(projectId),
      plannedDateLog: this.projectSerivce.getProjectPlannedDateLog(projectId),
      projectDetails: this.projectSerivce.getProjectById(projectId) // Fetch project details to get updated percentage
    }).pipe(
      catchError(error => {
        console.error('Error loading project data:', error);
        this.isChartsLoading = false; // Hide spinner on error
        return [];
      })
    ).subscribe(({ hourData, hourStatus, plannedDateLog, projectDetails }) => {
      // Process hour data
      if (hourData && hourData.data) {
        this.projectHourData.set(hourData.data);
      }

      // Process hour status
      if (hourStatus && hourStatus.data) {
        this.projectHourStatus.set(hourStatus.data);
        // Share the data with ProjectsService so hours-listing can use it without duplicate API call
        this.projectSerivce.setProjectHourStatusData(hourStatus.data);
        this.isLoading = false;
      }

      // Process planned date log
      if (plannedDateLog && plannedDateLog.data) {
        this.projectsPlannedDateLog.set(plannedDateLog.data[0]);
        this.goalDateTooltipRows.set(plannedDateLog.data);
        const responsibility = plannedDateLog.data[0].responsibility || '';
        if (responsibility) {
          // Normalize responsibility value from API to match responsibilityOptions format
          this.selectedResponsibility = this.getResponsibilityName(responsibility);
        }
      }

      // Process project details to update percentage when status changes
      if (projectDetails && projectDetails.data) {
        const projectPercentage = projectDetails.data.projectPercentageComplete || 0;
        if (projectPercentage !== undefined && projectPercentage !== null) {
          this.projectPercentage = typeof projectPercentage === 'string' ? Number(projectPercentage) : projectPercentage;
          this.projectSerivce.setProjectPercentage(projectPercentage.toString());
        }
      }

      // Initialize or update charts after all data is loaded
      // Try to update existing charts first, recreate if needed
      if (this.projectStatusChart && this.balanceHoursChart) {
        try {
          const hourStatus = this.projectHourStatus();
          const currentPercentage = this.projectPercentage;
          if (hourStatus && currentPercentage !== undefined && currentPercentage !== null) {
            this.updateProjectStatusChart(currentPercentage);
            this.updateBalanceHoursChart(hourStatus);
            // Hide spinner after successful update
            this.isChartsLoading = false;
            this.clearTimeouts();
            return; // Successfully updated
          } else {
            // Data is invalid, hide spinner and recreate charts
            this.isChartsLoading = false;
          }
        } catch (error) {
          console.warn('Chart update failed during data load, recreating:', error);
          // Spinner will be handled by checkAndInitializeCharts below
        }
      }

      // Charts don't exist or update failed, recreate them
      this.destroyCharts();
      const timeout = this.trackTimeout(setTimeout(() => {
        this.checkAndInitializeCharts();
      }, 50));
    });
  }

  // Individual API call methods removed - now consolidated in loadAllProjectData

  // Simplified chart initialization with consolidated retry logic
  private async checkAndInitializeCharts(): Promise<void> {
    // Clear any existing timeouts
    this.clearTimeouts();

    // Check if we're ready for chart initialization
    if (this.isReadyForChartInitialization()) {
      await this.initializeChartsWithRetry();
    } else {
      // If we have data but DOM elements aren't ready, retry with backoff
      if (this.selectedTab === 'hours' &&
          this.projectHourStatus() &&
          this.projectHourData() &&
          this.projectPercentage !== undefined &&
          this.projectPercentage !== null) {
        this.isChartsLoading = true;
        this.retryChartInitialization(0);
      } else {
        // Keep spinner visible if we can't initialize charts yet
        if (this.selectedTab === 'hours') {
          this.isChartsLoading = true;
        }
      }
    }
  }

  /**
   * Consolidated chart initialization with retry and error handling
   */
  private async initializeChartsWithRetry(attempt: number = 0): Promise<void> {
    const maxRetries = 3;
    const baseDelay = 100;

    this.isChartsLoading = true;
    this.destroyCharts(); // Ensure clean state

    // Add timeout to hide spinner if charts take too long
    this.loadingTimeout = this.trackTimeout(setTimeout(() => {
      if (this.isChartsLoading) {
        this.isChartsLoading = false;
        console.warn('Chart initialization timeout');
      }
    }, 8000));

    try {
      // Use requestAnimationFrame to ensure DOM is fully ready
      await new Promise(resolve => requestAnimationFrame(resolve));
      
      // Additional small delay to ensure DOM elements are properly rendered
      await new Promise(resolve => setTimeout(resolve, 50));

      await this.initializeCharts();
      this.clearTimeouts(); // Clear timeout if charts load successfully
    } catch (error) {
      this.clearTimeouts();
      
      // Retry with exponential backoff if attempts remain
      if (attempt < maxRetries) {
        const delay = baseDelay * Math.pow(2, attempt);
        this.retryTimeout = this.trackTimeout(setTimeout(() => {
          this.initializeChartsWithRetry(attempt + 1);
        }, delay));
      } else {
        this.handleChartInitializationFailure(error, 'initializeChartsWithRetry');
      }
    }
  }

  /**
   * Handle window resize efficiently using Highcharts reflow()
   * Only recreates charts if they don't exist
   */
  private handleChartResize(): void {
    if (this.selectedTab !== 'hours') return;

    // If charts exist, use reflow for efficient resize
    if (this.projectStatusChart && this.balanceHoursChart) {
      try {
        this.projectStatusChart.reflow();
        this.balanceHoursChart.reflow();
        return;
      } catch (error) {
        console.warn('Chart reflow failed, recreating charts:', error);
        // Fall through to recreate if reflow fails
      }
    }

    // If charts don't exist, initialize them
    if (!this.projectStatusChart || !this.balanceHoursChart) {
      this.checkAndInitializeCharts();
    }
  }

  // Public method to force chart initialization (can be called from parent component)
  public forceChartInitialization(): void {
    this.destroyCharts();
    const timeout = this.trackTimeout(setTimeout(async () => {
      await this.checkAndInitializeCharts();
    }, 200));
  }

  // Debug method to toggle spinner
  public toggleSpinner(): void {
    this.isChartsLoading = !this.isChartsLoading;
  }

  // Method to handle chart initialization failures
  private handleChartInitializationFailure(error: any, context: string): void {
    console.error(`Chart initialization failed in ${context}:`, error);
    this.isChartsLoading = false;
    this.hasError = true;

    // Check if it's a Highcharts module loading error
    if (error.message && error.message.includes('Class extends value undefined')) {
      console.warn('Highcharts module loading error detected, attempting alternative approach...');
      this.attemptAlternativeChartInitialization();
    } else {
      // Attempt to recover after a delay for other errors
      setTimeout(() => {
        this.hasError = false;
        if (this.selectedTab === 'hours') {
          this.forceChartInitialization();
        }
      }, 3000);
    }
  }

  // Alternative chart initialization when module loading fails
  private attemptAlternativeChartInitialization(): void {
    // Try to initialize charts without the modules (basic charts)
    setTimeout(() => {
      try {
        this.initializeBasicCharts();
        this.hasError = false;
      } catch (error) {
        console.error('Alternative chart initialization also failed:', error);
        this.isChartsLoading = false;
      }
    }, 1000);
  }

  // Initialize basic charts without solidgauge module
  private initializeBasicCharts(): void {
    if (this.selectedTab === 'hours') {
      // Add validation to ensure data and DOM elements are available
      if (this.projectHourStatus() && this.projectHourData() &&
          this.projectStatusChartContainer?.nativeElement &&
          this.balanceHoursChartContainer?.nativeElement) {

        // Clear any existing charts first
        this.destroyCharts();

        // Initialize basic charts (pie charts instead of solidgauge)
        try {
          this.initBasicProjectStatusChart();
        } catch (error) {
          console.error('Error initializing basic project status chart:', error);
        }

        try {
          this.initBasicBalanceHoursChart();
        } catch (error) {
          console.error('Error initializing basic balance hours chart:', error);
        }

        this.isChartsLoading = false;
      } else {
        this.isChartsLoading = false;
      }
    }
  }

  // Method to validate if all required data and DOM elements are available
  private isReadyForChartInitialization(): boolean {
    return !!(
      this.selectedTab === 'hours' &&
      this.projectHourStatus() &&
      this.projectHourData() &&
      this.projectStatusChartContainer?.nativeElement &&
      this.balanceHoursChartContainer?.nativeElement &&
      this.projectPercentage !== undefined &&
      this.projectPercentage !== null
    );
  }

  // Method to handle component visibility changes (useful for navigation)
  public onComponentVisible(): void {
    if (this.selectedTab === 'hours' && !this.isChartsLoading) {
      // Small delay to ensure DOM is ready after navigation
      setTimeout(async () => {
        await this.checkAndInitializeCharts();
      }, 100);
    }
  }

  // Methods
  selectTab(tab: string) {
    this.selectedTab = tab;
    this.destroyCharts();

    // Set loading state immediately for notes tab
    if (tab === 'other') {
      this.notesLoading.set(true);
    }

    setTimeout(async () => {
      if (tab === 'hours') {
        // Use the consolidated chart initialization logic
        await this.checkAndInitializeCharts();
      }else if (tab === 'other') {
        // Load notes when notes tab is selected
        this.loadNotes();
        // Hide spinner for notes tab
        this.isChartsLoading = false;
      }else {
        // Hide spinner for other tabs
        this.isChartsLoading = false;
      }
    }, 150); // Increased delay for better reliability
  }

  private destroyCharts() {
    // Clear any pending timeouts first
    this.clearTimeouts();

    // Destroy individual chart references
    try {
      if (this.projectStatusChart && typeof this.projectStatusChart.destroy === 'function') {
        this.projectStatusChart.destroy();
      }
    } catch (error) {
      console.warn('Error destroying project status chart:', error);
    }
    this.projectStatusChart = null;

    try {
      if (this.balanceHoursChart && typeof this.balanceHoursChart.destroy === 'function') {
        this.balanceHoursChart.destroy();
      }
    } catch (error) {
      console.warn('Error destroying balance hours chart:', error);
    }
    this.balanceHoursChart = null;

    // Also destroy any charts in the instances array (backward compatibility)
    this.chartInstances.forEach(chart => {
      try {
        if (chart && typeof chart.destroy === 'function') {
          chart.destroy();
        }
      } catch (error) {
        console.warn('Error destroying chart:', error);
      }
    });
    this.chartInstances = [];

    // Clear chart containers to ensure clean state
    if (this.projectStatusChartContainer?.nativeElement) {
      this.projectStatusChartContainer.nativeElement.innerHTML = '';
    }
    if (this.balanceHoursChartContainer?.nativeElement) {
      this.balanceHoursChartContainer.nativeElement.innerHTML = '';
    }
  }

  onDateChange() {
    // Update the projectsPlannedDateLog signal when the goal date changes
    if (this.selectedGoalDate) {
      const currentData = this.projectsPlannedDateLog();
      if (currentData) {
        const updatedData = {
          ...currentData,
          currentProjectedDate: this.selectedGoalDate,
          updatedAt: new Date(),
          changedBy: {
            name: 'Current User' // This should come from your auth service
          }
        };

        this.projectsPlannedDateLog.set(updatedData);

        // Optionally save the change to the API
        // this.saveProjectCompletionDate({
        //   completionDate: this.selectedGoalDate,
        //   responsibleTeam: this.selectedResponsibility,
        //   notes: 'Date changed via inline datepicker'
        // });
      }
    }
  }

  openDatepicker(event: Event) {
    event.stopPropagation();
    this.datepicker.open();
  }

  openProjectCompletionModal(): void {
    //data for the modal slider
    const modalData = {
      currentProjectedDate: this.projectsPlannedDateLog()?.currentProjectedDate?.[0]
        ? new Date(this.projectsPlannedDateLog().currentProjectedDate)
        : undefined,
      currentResponsibility: this.selectedResponsibility,
      projectId: this.currentProjectId || undefined,
      responsibilities: this.responsibilityOptions.map(option => ({
        name: option.name,
        id: this.generateResponsibilityId(option.name)
      }))
    };

    this.projectCompletionModalData.set(modalData);
    this.showProjectCompletionSlider.set(true);
  }

  onProjectCompletionModalClosed(): void {
    this.showProjectCompletionSlider.set(false);
  }

  onProjectCompletionFormSubmitted(result: any): void {
    if (result) {
      // Handle the result when the form is submitted
      this.updateProjectCompletion(result);
      if (result.completionDate) {
        // Parse the date string back to Date object
        const newDate = new Date(result.completionDate);

        // Update the selectedGoalDate
        this.selectedGoalDate = newDate;

        // Update the projectsPlannedDateLog signal with new data
        const currentData = this.projectsPlannedDateLog();
        if (currentData) {
          const updatedData = {
            ...currentData,
            currentProjectedDate: newDate,
            updatedAt: new Date(),
            responsibility: this.getResponsibilityName(result.responsibleTeam),
            notes: result.notes
          };

          this.projectsPlannedDateLog.set(updatedData);

          // Update the selected responsibility - normalize to proper name format
          if (result.responsibleTeam) {
            this.selectedResponsibility = this.getResponsibilityName(result.responsibleTeam);
          }

          // Make an API call to save the changes
          this.saveProjectCompletionDate(result);
        }
      }
    }
  }

  private updateProjectCompletion(data: any): void {
    if (!this.currentProjectId) {
      this.toastrService.error('No current project ID available');
      return;
    }

    // Prepare the request body according to the API specification
    const requestBody = {
      projectID: this.currentProjectId,
      currentProjectedDate: this.formatDateForAPI(data.completionDate),
      reason: data.notes,
      responsibility: this.getResponsibilityName(data.responsibleTeam)
    };
    this.isLoading = true;

    this.projectSerivce.createProjectPlannedDateLog(requestBody).subscribe({
      next: (response) => {

        this.isLoading = false;

        // Show success notification
        this.toastrService.success(response.message || 'Project completion date updated successfully',
        );

        // Refresh the project data after successful update
        this.loadAllProjectData(this.currentProjectId!);

        this.projectSerivce.triggerProjectDataRefresh();

        // Close the modal after successful save
        this.showProjectCompletionSlider.set(false);
      },
      error: (error) => {
        console.error('Error updating project completion:', error);
        this.isLoading = false;

        // Show error notification
        this.toastrService.error(
          error.error?.message || 'Failed to update project completion date',
          'Error'
        );

        // Close the modal even on error (user can try again)
        this.showProjectCompletionSlider.set(false);
      }
    });
  }

    private formatDateForAPI(dateString: string): string {
    // Convert date string like "May 19, 2025" to "2025-05-19" format
    if (!dateString) return '';

    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }

  /**
   * Generate a consistent ID from responsibility name
   * Handles spaces around dashes properly (e.g., "TW - INTL" -> "tw-intl")
   */
  private generateResponsibilityId(name: string): string {
    if (!name) return '';
    
    // Remove spaces around dashes first, then normalize
    // "TW - INTL" -> "TW-INTL" -> "tw-intl"
    let normalized = name.trim();
    
    // Replace " - " (space-dash-space) with single dash
    normalized = normalized.replace(/\s*-\s*/g, '-');
    
    // Replace remaining spaces with dashes
    normalized = normalized.replace(/\s+/g, '-');
    
    // Convert to lowercase and collapse multiple dashes
    normalized = normalized.toLowerCase().replace(/-+/g, '-');
    
    return normalized;
  }

  /**
   * Convert responsibility ID back to proper name format
   * Handles various ID formats and matches them to responsibilityOptions
   */
  private getResponsibilityName(responsibilityId: string): string {
    if (!responsibilityId) return '';
    
    // Normalize the input to handle various formats
    const normalizedInput = responsibilityId.toLowerCase().trim().replace(/-+/g, '-');
    
    // Find the responsibility name based on the ID
    // Try exact match first (in case API returns the actual name)
    let responsibility = this.responsibilityOptions.find(option =>
      option.name === responsibilityId
    );
    
    // Try matching by generated ID
    if (!responsibility) {
      responsibility = this.responsibilityOptions.find(option => {
        const optionId = this.generateResponsibilityId(option.name);
        return optionId === normalizedInput;
      });
    }
    
    // Try case-insensitive match of the name itself
    if (!responsibility) {
      responsibility = this.responsibilityOptions.find(option =>
        option.name.toLowerCase().trim() === normalizedInput ||
        this.generateResponsibilityId(option.name) === normalizedInput
      );
    }

    return responsibility ? responsibility.name : responsibilityId;
  }

  /**
   * Save the project completion date to the API
   */
  private saveProjectCompletionDate(completionData: any): void {
    // Get the current project ID from the route or component input
    const projectId = this.getCurrentProjectId();

    if (!projectId) {
      return;
    }

    const payload = {
      projectId: projectId,
      completionDate: completionData.completionDate,
      responsibleTeam: completionData.responsibleTeam,
      notes: completionData.notes
    };
  }

  /**
   * Get the current project ID from route params or component input
   */
  private getCurrentProjectId(): string | null {
    // This should return the current project ID
    // You might need to adjust this based on how you're getting the project ID
    // For example, if it's passed as an input to this component:
    // return this.projectId;

    // Or if it's in the route params:
    // return this.route.snapshot.params['id'];

    // For now, returning null - you'll need to implement this based on your setup
    return null;
  }

  ngAfterViewInit(): void {
    // Add initial check for charts if hours tab is selected by default
    // This ensures DOM elements are available before trying to initialize charts
    setTimeout(async () => {
      await this.checkAndInitializeCharts();
    }, 300); // Increased delay to ensure DOM is fully ready

    fromEvent(window, 'resize').pipe(
      debounceTime(300),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      // Use Highcharts reflow() for efficient resize handling
      this.handleChartResize();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['projectData'] && !changes['projectData'].firstChange) {
      this.reloadCharts();
    }
  }

  private destroy$ = new Subject<void>();
  private lastRefreshCount = 0; // Track last refresh count to detect changes
  private lastHourStatusHash = ''; // Track last hour status hash to detect actual value changes

  public async reloadCharts(): Promise<void> {
    this.destroyCharts();
    await this.initializeCharts();
  }

  /**
   * Update existing charts with fresh data using chart.update() when possible
   * Falls back to recreation only if charts don't exist or update fails
   */
  private updateChartsWithFreshData(): void {
    if (this.selectedTab !== 'hours') return;

    // Clear any pending update timeout
    if (this.updateTimeout) {
      clearTimeout(this.updateTimeout);
      this.updateTimeout = null;
    }

    // Get fresh data to validate
    const hourStatus = this.projectHourStatus();
    const currentPercentage = this.projectPercentage;

    // Only update if we have valid data
    if (!hourStatus ||
        currentPercentage === undefined ||
        currentPercentage === null ||
        isNaN(currentPercentage)) {
      // Ensure spinner is hidden if data is invalid
      this.isChartsLoading = false;
      return;
    }

    // Debounce updates to avoid excessive calls
    this.updateTimeout = this.trackTimeout(setTimeout(() => {
      this.updateTimeout = null;
      
      // Try to update existing charts first
      const canUpdate = this.projectStatusChart && this.balanceHoursChart;
      
      if (canUpdate) {
        try {
          this.updateProjectStatusChart(currentPercentage);
          this.updateBalanceHoursChart(hourStatus);
          // Hide spinner after successful update
          this.isChartsLoading = false;
          this.clearTimeouts();
          return; // Successfully updated, no need to recreate
        } catch (error) {
          console.warn('Chart update failed, recreating charts:', error);
          // Ensure spinner is shown for recreation
          this.isChartsLoading = true;
          // Fall through to recreate if update fails
        }
      } else {
        // Charts don't exist, ensure spinner is shown for initialization
        this.isChartsLoading = true;
      }

      // If charts don't exist or update failed, recreate them
      this.destroyCharts();
      this.checkAndInitializeCharts();
    }, 50));
  }

  /**
   * Get the color for project status chart based on percentage
   * Returns #7CC74D when status is 100%, otherwise uses primary color
   */
  private getProjectStatusChartColor(percentage: number): string {
    return percentage === 100 ? '#7CC74D' : this.chartConfig.colors.primary;
  }

  /**
   * Update project status chart data using chart.update()
   */
  private updateProjectStatusChart(percentage: number): void {
    if (!this.projectStatusChart) return;

    const clampedPercentage = Math.max(0, Math.min(100, percentage));
    const chartColor = this.getProjectStatusChartColor(clampedPercentage);
    
    this.projectStatusChart.series[0].update({
      type: 'solidgauge',
      data: [{
        y: clampedPercentage,
        color: chartColor,
        radius: '100%',
        innerRadius: '88%'
      }],
      dataLabels: {
        enabled: true,
        formatter: function() {
          return `${clampedPercentage}%`;
        }
      }
    } as any, true);
  }

  /**
   * Update balance hours chart data using chart.update()
   */
  private updateBalanceHoursChart(hourStatus: any): void {
    if (!this.balanceHoursChart) return;

    const unfilledHours = hourStatus.unfilledHours;
    const totalPlannedHours = hourStatus.totalPlannedHours || 1;
    
    if (totalPlannedHours === 0) {
      return; // Skip update if no planned hours
    }

    const percent = Math.abs(unfilledHours) / totalPlannedHours * 100;
    const redArcLength = Math.min(percent, 100); // Cap at 100% for yAxis max

    const currentHourStatus = this.projectHourStatus();
    const currentUnfilledHours = currentHourStatus?.unfilledHours ?? unfilledHours;

    this.balanceHoursChart.series[0].update({
      type: 'solidgauge',
      data: [{
        y: redArcLength,
        color: this.chartConfig.colors.secondary,
        radius: '85%',
        innerRadius: '68%'
      }],
      dataLabels: {
        enabled: true,
        formatter: () => {
          return `<div style="display: flex; align-items: center; justify-content: center; height: 100%; text-align: center;">
                    <div>
                      <div class="balance-main-text" style="font-size: 11px; font-weight: 500; line-height: 1.1;">Balance<br>Hours</div>
                      <div class="balance-sub-text" style="font-size: 12px; font-weight: 600; ">${currentUnfilledHours.toFixed(2)}</div>
                    </div>
                </div>`;
        }
      }
    } as any, true);
  }

  /**
   * Setup cross-tab synchronization to update charts when data changes in other tabs
   * Uses switchMap to properly handle project changes and prevent memory leaks
   */
  private setupCrossTabSync(): void {
    // Listen to all sync events and check projectId manually
    // This approach is simpler and more reliable than combineLatest
    this.crossTabSyncService.getSyncEvents()
      .pipe(
        takeUntil(this.destroy$),
        // Filter out null events
        filter((event): event is CrossTabSyncEvent => {
          return !!event;
        })
      )
      .subscribe((event) => {
        // Skip if component not initialized
        if (!this.isComponentInitialized) {
          return;
        }

        // Get projectId from event (check both top level and data)
        const eventProjectId = event.projectId || event.data?.projectId;
        
        // Get current projectId - use the latest value from the BehaviorSubject
        const currentProjectId = this.currentProjectId;
        
        // Process events that affect project hours/charts for the current project
        const shouldRefresh = (
          (event.type === 'project_hours_updated' ||
           event.type === 'project_status_updated' ||
           event.type === 'task_status_updated' ||
           event.type === 'task_updated' ||
           event.type === 'project_updated') &&
          eventProjectId === currentProjectId &&
          currentProjectId !== null &&
          this.selectedTab === 'hours'
        );

        if (shouldRefresh) {
          // Reload all project data to get fresh values
          this.loadAllProjectData(currentProjectId);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroyCharts();
    this.clearTimeouts(); // Clear all tracked timeouts to prevent memory leaks
    this.destroy$.next();
    this.destroy$.complete();
    if (this.hideTooltipTimeout) {
      clearTimeout(this.hideTooltipTimeout);
      this.hideTooltipTimeout = null;
    }
  }



  /**
   * Initialize charts with improved error handling for partial failures
   */
  private async initializeCharts(): Promise<void> {
    if (this.selectedTab !== 'hours') {
      this.isChartsLoading = false;
      return;
    }

    // Validate data and DOM elements
    if (!this.projectHourStatus() || !this.projectHourData() ||
        !this.projectStatusChartContainer?.nativeElement ||
        !this.balanceHoursChartContainer?.nativeElement) {
      this.isChartsLoading = false;
      return;
    }

    // Ensure modules are loaded before initializing charts
    const modulesReady = await loadHighchartsModules();
    const initializationResults: { projectStatus: boolean; balanceHours: boolean } = {
      projectStatus: false,
      balanceHours: false
    };

    try {
      // Initialize project status chart
      try {
        this.initProjectStatusChart();
        initializationResults.projectStatus = true;
      } catch (error) {
        console.error('Error initializing project status chart:', error);
        // Try fallback
        try {
          this.initBasicProjectStatusChart();
          initializationResults.projectStatus = true;
        } catch (fallbackError) {
          console.error('Fallback project status chart also failed:', fallbackError);
        }
      }

      // Initialize balance hours chart
      try {
        this.initBalanceHoursChart();
        initializationResults.balanceHours = true;
      } catch (error) {
        console.error('Error initializing balance hours chart:', error);
        // Try fallback
        try {
          this.initBasicBalanceHoursChart();
          initializationResults.balanceHours = true;
        } catch (fallbackError) {
          console.error('Fallback balance hours chart also failed:', fallbackError);
        }
      }

      // Handle partial initialization success
      if (initializationResults.projectStatus && initializationResults.balanceHours) {
        this.isChartsLoading = false;
        this.hasError = false;
      } else if (initializationResults.projectStatus || initializationResults.balanceHours) {
        console.warn('Partial chart initialization - some charts failed');
        this.isChartsLoading = false;
        this.hasError = true; // Set error flag but don't block UI
      } else {
        throw new Error('All chart initialization attempts failed');
      }

      // Add fallback to hide spinner after a short delay
      this.fallbackTimeout = this.trackTimeout(setTimeout(() => {
        if (this.isChartsLoading) {
          this.isChartsLoading = false;
        }
      }, 2000));
    } catch (error) {
      console.error('Error initializing charts:', error);
      this.isChartsLoading = false;
      this.hasError = true;
      throw error; // Re-throw to allow retry logic
    }
  }


  // Method to update chart data from JSON
  public updateChartData(data: ProjectStatusData): void {
    this.projectData = { ...data };
    this.reloadCharts();
  }

  // Test method to verify charts are working


  private getBaseChartOptions(): Partial<Highcharts.Options> {
    return {
      chart: {
        type: 'solidgauge',
        backgroundColor: this.chartConfig.backgroundColor,
        height: this.getResponsiveChartSize().height,
        width: this.getResponsiveChartSize().width,
        margin: [0, 0, 0, 0],
        spacing: [0, 0, 0, 0]
      },
      title: { text: undefined },
      credits: { enabled: false },
      tooltip: { enabled: false },
      accessibility: { enabled: false },
      plotOptions: {
        solidgauge: {
          linecap: 'round',
          rounded: true,
          stickyTracking: false
        }
      }
    };
  }

  private initBalanceHoursChart(): void {
    if (!this.balanceHoursChartContainer?.nativeElement) {
      this.isChartsLoading = false; // Hide spinner if container not available
      return;
    }

    // Get fresh hour status data - ensure we're using the latest data
    const hourStatus = this.projectHourStatus();
    if (!hourStatus ||
        hourStatus.unfilledHours === undefined ||
        hourStatus.totalPlannedHours === undefined ||
        isNaN(hourStatus.unfilledHours) ||
        isNaN(hourStatus.totalPlannedHours)) {
      this.isChartsLoading = false; // Hide spinner if data is invalid
      return;
    }

    try {
      // Use fresh values for calculation
      const unfilledHours = hourStatus.unfilledHours;
      const totalPlannedHours = hourStatus.totalPlannedHours || 1;
      const percent = Math.abs(unfilledHours) / totalPlannedHours * 100;
      const redArcLength = percent; // Full value, no limit


      const chartOptions: Highcharts.Options = {
        ...this.getBaseChartOptions(),
        pane: {
          startAngle: 0,
          endAngle: 360,
          background: [{
            outerRadius: '100%',
            innerRadius: '75%',
            backgroundColor: '#7cc74d',
            shape: 'arc',
            borderWidth: 0
          }]
        },
        yAxis: {
          min: 0,
          max: 100,
          lineWidth: 0,
          tickPositions: [],
          labels: { enabled: false }
        },
        plotOptions: {
          solidgauge: {
            dataLabels: {
              enabled: true,
              borderWidth: 0,
              useHTML: true,
              y: 0,
              verticalAlign: 'middle',
              align: 'center',
              formatter: () => {
                // Get fresh value from signal to ensure latest data
                const currentHourStatus = this.projectHourStatus();
                const currentUnfilledHours = currentHourStatus?.unfilledHours ?? unfilledHours;
                return `<div style="display: flex; align-items: center; justify-content: center; height: 100%; text-align: center;">
                          <div>
                            <div class="balance-main-text" style="font-size: 11px; font-weight: 500; line-height: 1.1;">Balance<br>Hours</div>
                            <div class="balance-sub-text" style="font-size: 12px; font-weight: 600; ">${currentUnfilledHours.toFixed(2)}</div>
                          </div>
                      </div>`;
              }
            },
            linecap: 'round',
            rounded: true,
            stickyTracking: false
          }
        },
        series: [{
          type: 'solidgauge',
          name: 'Balance',
          data: [{
            y: redArcLength,
            color: this.chartConfig.colors.secondary,
            radius: '85%',
            innerRadius: '68%'
          }]
        }]
      };

      const chart = Highcharts.chart(this.balanceHoursChartContainer.nativeElement, chartOptions);
      this.balanceHoursChart = chart;
      this.chartInstances.push(chart);

      // Check if both charts are ready and hide spinner
      this.checkChartsReady();
    } catch (error) {
      console.error('Error creating balance hours chart:', error);
      this.isChartsLoading = false; // Hide spinner on error
    }
  }

  // Basic balance hours chart (fallback when solidgauge module fails)
  private initBasicBalanceHoursChart(): void {
    if (!this.balanceHoursChartContainer?.nativeElement) {
      return;
    }

    // Get fresh hour status data
    const hourStatus = this.projectHourStatus();
    if (!hourStatus ||
        hourStatus.unfilledHours === undefined ||
        hourStatus.totalPlannedHours === undefined ||
        isNaN(hourStatus.unfilledHours) ||
        isNaN(hourStatus.totalPlannedHours)) {
      return;
    }

    try {
      // Use fresh values for calculation
      const unfilledHours = hourStatus.unfilledHours;
      const totalPlannedHours = hourStatus.totalPlannedHours || 1;
      const percent = Math.abs(unfilledHours) / totalPlannedHours * 100;
      const redArcLength = Math.min(percent, 35);

      const chartOptions: Highcharts.Options = {
        chart: {
          type: 'solidgauge',
          backgroundColor: this.chartConfig.backgroundColor,
          height: this.getResponsiveChartSize().height,
          width: this.getResponsiveChartSize().width,
          margin: [0, 0, 0, 0],
          spacing: [0, 0, 0, 0]
        },
        title: { text: undefined },
        credits: { enabled: false },
        tooltip: { enabled: false },
        accessibility: { enabled: false },
        pane: {
          startAngle: 0,
          endAngle: 360,
          background: [{
            outerRadius: '100%',
            innerRadius: '75%',
            backgroundColor: '#7cc74d',
            shape: 'arc',
            borderWidth: 0
          }]
        },
        yAxis: {
          min: 0,
          max: 100,
          lineWidth: 0,
          tickPositions: [],
          labels: { enabled: false }
        },
        plotOptions: {
          solidgauge: {
            dataLabels: {
              enabled: true,
              borderWidth: 0,
              useHTML: true,
              y: 0,
              verticalAlign: 'middle',
              align: 'center',
              formatter: () => {
                // Get fresh value from signal to ensure latest data
                const currentHourStatus = this.projectHourStatus();
                const currentUnfilledHours = currentHourStatus?.unfilledHours ?? unfilledHours;
                return `<div style="display: flex; align-items: center; justify-content: center; height: 100%; text-align: center;">
                          <div>
                            <div class="balance-main-text" style="font-size: 11px; font-weight: 500; line-height: 1.1;">Balance<br>Hours</div>
                            <div class="balance-sub-text" style="font-size: 12px; font-weight: 600; ">${currentUnfilledHours.toFixed(2)}</div>
                          </div>
                      </div>`;
              }
            },
            linecap: 'round',
            rounded: true,
            stickyTracking: false
          }
        },
        series: [{
          type: 'solidgauge',
          name: 'Balance',
          data: [{
            y: redArcLength,
            color: this.chartConfig.colors.secondary,
            radius: '85%',
            innerRadius: '68%'
          }]
        }]
      };

      const chart = Highcharts.chart(this.balanceHoursChartContainer.nativeElement, chartOptions);
      this.balanceHoursChart = chart;
      this.chartInstances.push(chart);
    } catch (error) {
      console.error('Error creating basic balance hours chart:', error);
    }
  }

  private initProjectStatusChart(): void {
    if (!this.projectStatusChartContainer?.nativeElement) {
      this.isChartsLoading = false; // Hide spinner if container not available
      return;
    }

    // Get fresh percentage value - ensure we're using the latest data
    const currentPercentage = this.projectPercentage;

    // Add data validation
    if (currentPercentage === undefined || currentPercentage === null || isNaN(currentPercentage)) {
      this.isChartsLoading = false; // Hide spinner if data is invalid
      return;
    }

    try {
      const chartColor = this.getProjectStatusChartColor(currentPercentage);
      const chartOptions: Highcharts.Options = {
        ...this.getBaseChartOptions(),
        pane: {
          startAngle: -150,
          endAngle: 150,
          background: [{
            outerRadius: '100%',
            innerRadius: '88%',
            backgroundColor: this.chartConfig.colors.background,
            shape: 'arc',
            borderWidth: 0
          }]
        },
        yAxis: {
          min: 0,
          max: 100,
          lineWidth: 0,
          tickPositions: [],
          labels: { enabled: false }
        },
        series: [{
          type: 'solidgauge',
          name: 'Completion',
          data: [{
            y: currentPercentage, // Use fresh value
            color: chartColor,
            radius: '100%',
            innerRadius: '88%'
          }],
          dataLabels: {
            enabled: true,
            borderWidth: 0,
            useHTML: false,
            y: 0,
            verticalAlign: 'middle',
            align: 'center',
            formatter: function () {
              return `${this.y}%`;
            },
            style: {
              fontSize: '20px',
              fontWeight: '600',
              color: 'var(--color-text)',
              fontFamily: 'Outfit, sans-serif',
            }
          }
        }]
      };

      const chart = Highcharts.chart(this.projectStatusChartContainer.nativeElement, chartOptions);
      this.projectStatusChart = chart;
      this.chartInstances.push(chart);

      // Check if both charts are ready and hide spinner
      this.checkChartsReady();
    } catch (error) {
      console.error('Error creating project status chart:', error);
      this.isChartsLoading = false; // Hide spinner on error
    }
  }

  // Basic project status chart (fallback when solidgauge module fails)
  private initBasicProjectStatusChart(): void {
    if (!this.projectStatusChartContainer?.nativeElement) {
      return;
    }

    if (this.projectPercentage === undefined || this.projectPercentage === null) {
      return;
    }

    try {
      const chartColor = this.getProjectStatusChartColor(this.projectPercentage);
      const chartOptions: Highcharts.Options = {
        chart: {
          type: 'solidgauge',
          backgroundColor: this.chartConfig.backgroundColor,
          height: this.getResponsiveChartSize().height,
          width: this.getResponsiveChartSize().width,
          margin: [0, 0, 0, 0],
          spacing: [0, 0, 0, 0]
        },
        title: { text: undefined },
        credits: { enabled: false },
        tooltip: { enabled: false },
        accessibility: { enabled: false },
        pane: {
          startAngle: -150,
          endAngle: 150,
          background: [{
            outerRadius: '100%',
            innerRadius: '88%',
            backgroundColor: this.chartConfig.colors.background,
            shape: 'arc',
            borderWidth: 0
          }]
        },
        yAxis: {
          min: 0,
          max: 100,
          lineWidth: 0,
          tickPositions: [],
          labels: { enabled: false }
        },
        series: [{
          type: 'solidgauge',
          name: 'Completion',
          data: [{
            y: this.projectPercentage,
            color: chartColor,
            radius: '100%',
            innerRadius: '88%'
          }],
          dataLabels: {
            enabled: true,
            borderWidth: 0,
            useHTML: false,
            y: 0,
            verticalAlign: 'middle',
            align: 'center',
            formatter: function () {
              return `${this.y}%`;
            },
            style: {
              fontSize: '20px',
              fontWeight: '600',
              color: 'var(--color-text)',
              fontFamily: 'Outfit, sans-serif',
            }
          }
        }]
      };

      const chart = Highcharts.chart(this.projectStatusChartContainer.nativeElement, chartOptions);
      this.projectStatusChart = chart;
      this.chartInstances.push(chart);
    } catch (error) {
      console.error('Error creating basic project status chart:', error);
    }
  }

  private getResponsiveChartSize() {
    return {
      height: 100,
      width: 100
    };
  }

  /**
   * Check if charts are ready with improved error handling for partial initialization
   */
  private checkChartsReady(): void {
    // Check if we have valid chart instances
    const hasProjectStatusChart = this.projectStatusChart && typeof this.projectStatusChart.destroy === 'function';
    const hasBalanceHoursChart = this.balanceHoursChart && typeof this.balanceHoursChart.destroy === 'function';
    
    // Hide spinner if at least one chart is ready (graceful degradation)
    if (hasProjectStatusChart || hasBalanceHoursChart) {
      this.isChartsLoading = false;
      this.clearTimeouts();
      
      // Log warning if only partial initialization succeeded
      if (!hasProjectStatusChart || !hasBalanceHoursChart) {
        console.warn('Partial chart initialization:', {
          projectStatus: hasProjectStatusChart,
          balanceHours: hasBalanceHoursChart
        });
      }
    }
  }

  // Consolidated timeout management - track and clear all timeouts
  private clearTimeouts(): void {
    // Clear all tracked timeouts
    this.activeTimeouts.forEach(timeout => {
      if (timeout) {
        clearTimeout(timeout);
      }
    });
    this.activeTimeouts.clear();
    
    // Clear individual timeout references
    if (this.loadingTimeout) {
      clearTimeout(this.loadingTimeout);
      this.loadingTimeout = null;
    }
    if (this.fallbackTimeout) {
      clearTimeout(this.fallbackTimeout);
      this.fallbackTimeout = null;
    }
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
      this.retryTimeout = null;
    }
    if (this.updateTimeout) {
      clearTimeout(this.updateTimeout);
      this.updateTimeout = null;
    }
  }

  // Helper to track timeouts for cleanup
  private trackTimeout(timeout: any): any {
    this.activeTimeouts.add(timeout);
    return timeout;
  }

  /**
   * Simplified retry logic for chart initialization when DOM isn't ready
   */
  private async retryChartInitialization(attempt: number): Promise<void> {
    const maxRetries = 8;
    const baseDelay = 150;

    if (attempt >= maxRetries) {
      console.warn('Max retries reached for chart initialization');
      this.isChartsLoading = false;
      return;
    }

    // Check if DOM elements are now available
    if (this.projectStatusChartContainer?.nativeElement &&
        this.balanceHoursChartContainer?.nativeElement) {
      // DOM is ready, proceed with initialization
      await this.initializeChartsWithRetry();
    } else {
      // Calculate delay with exponential backoff, capped at 2 seconds
      const delay = Math.min(baseDelay * Math.pow(1.5, attempt), 2000);
      this.retryTimeout = this.trackTimeout(setTimeout(() => {
        this.retryChartInitialization(attempt + 1);
      }, delay));
    }
  }

  onDateSelected(date: Date): void {
    if (date) {
      this.selectedDate = date;

    }
  }

  toggleCalendar(): void {
    this.isCalendarOpen = !this.isCalendarOpen;
  }

  toggleTabContent(): void {
    this.isTabContentVisible = !this.isTabContentVisible;
  }

  private isValidData(data: ProjectStatusData): boolean {
    return data.projectCompletion > 0 || data.plannedHours > 0 || data.filledHours !== 0;
  }

  private isSameData(data: ProjectStatusData): boolean {
    return (
      this.projectData.projectCompletion === data.projectCompletion &&
      this.projectData.plannedHours === data.plannedHours &&
      this.projectData.filledHours === data.filledHours &&
      this.projectData.unfilledHours === data.unfilledHours &&
      this.projectData.offsetHours === data.offsetHours
    );
  }


  // Notes management signals

  notesList = signal<Note[]>([]);
  notesLoading = signal<boolean>(false);
  notesError = signal<string | null>(null);
  showAddNoteForm = signal(false);
  newNoteText = signal('');
  editingNoteId = signal<string | null>(null);
  showDeleteConfirmPopup = signal<boolean>(false);
  noteToDelete = signal<Note | null>(null);
  deleteNoteLoading = signal<boolean>(false);
  addNoteLoading = signal<boolean>(false);
  editNoteLoading = signal<boolean>(false);

  // Quill editor configuration for Quill 2.x
  // Theme is handled via CSS import and registration (not in modules config)
  // Handlers must be part of toolbar configuration when using custom handlers
  quillConfig: any = {
    toolbar: {
      container: [
      ['bold', 'italic', 'underline','blockquote'],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      ['link', 'image']
    ],
    handlers: {
      'image': this.customImageHandler.bind(this)
    }
    }
  };

  // File preview state
  showFilePreview = signal(false);
  currentFilePreview = signal<any | null>(null);
  
  // Track image thumbnail load state (noteId -> loaded successfully)
  imageThumbnailLoaded = signal<Set<string>>(new Set());

  // File upload state
  selectedFiles: File[] = [];
  isDragOver = false;
  maxFileSize = 10 * 1024 * 1024; // 10MB
  allowedFileTypes = [
    'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp',
    'video/mp4', 'video/avi', 'video/mov', 'video/wmv',
    'audio/mp3', 'audio/wav', 'audio/m4a', 'audio/ogg',
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation'
  ];

  // Custom image handler for Quill editor
  customImageHandler() {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx');
    input.setAttribute('multiple', 'true');
    input.style.display = 'none';

    input.onchange = () => {
      const files = input.files;
      if (files && files.length > 0) {
        this.handleQuillFiles(Array.from(files));
      }
    };

    document.body.appendChild(input);
    input.click();
    document.body.removeChild(input);
  }

  private handleQuillFiles(files: File[]) {
    const validFiles: File[] = [];
    const errors: string[] = [];

    files.forEach(file => {

      validFiles.push(file);
    });

    // Add valid files to selected files
    this.selectedFiles = [...this.selectedFiles, ...validFiles];

    // Show errors if any
    if (errors.length > 0) {
      this.toastrService.error(errors.join('<br>'), 'File Upload Errors');
    }

    // Show success message for valid files
    if (validFiles.length > 0) {
      this.toastrService.success(`${validFiles.length} file(s) added successfully`);
    }
  }

  // Computed sorted notes
  sortedNotes = computed(() => {
    const notes = this.notesList();
    return [...notes].sort((a, b) => {
      // First priority: pinned notes
      if (a.isPinned && !b.isPinned) return -1;
      if (!a.isPinned && b.isPinned) return 1;

      // Second priority: newly added notes (by creation time from API)
      // Use the raw createdAt timestamp for accurate sorting
      const aTime = new Date(a.createdAt || '').getTime();
      const bTime = new Date(b.createdAt || '').getTime();

      // Sort by most recent first (newest notes appear first)
      return bTime - aTime;
    });
  });

  /**
   * Handle responsibility change from the ng-select dropdown
   */
  onResponsibilityChange(newResponsibility: string): void {
    this.selectedResponsibility = newResponsibility;

    // Update the projectsPlannedDateLog signal with the new responsibility
    const currentData = this.projectsPlannedDateLog();
    if (currentData) {
      const updatedData = {
        ...currentData,
        responsibility: newResponsibility,
        updatedAt: new Date(),
        changedBy: {
          name: 'Current User' // This should come from your auth service
        }
      };

      this.projectsPlannedDateLog.set(updatedData);
    }
  }

  onGoalDateHintEnter() {
    if (this.hideTooltipTimeout) {
      clearTimeout(this.hideTooltipTimeout);
      this.hideTooltipTimeout = null;
    }
    this.showGoalDateTooltip = true;
    this.tooltipPositioned = false;
    this.tooltipPositionRetryCount = 0;
    // Use requestAnimationFrame to ensure tooltip is rendered before positioning
    requestAnimationFrame(() => {
      this.positionGoalDateTooltip();
    });
  }

  onGoalDateHintLeave() {
    this.hideTooltipTimeout = setTimeout(() => {
      this.showGoalDateTooltip = false;
      this.tooltipPositioned = false;
    }, 150);
  }

  /**
   * Format hours to display only 4 numbers total with max 2 decimal places (rounded)
   * Examples: 1234.56 -> 1234, 123.456 -> 123.46, 12.3456 -> 12.34, 1.23456 -> 1.23
   */
  formatHours(value: number | null | undefined): string {
    if (value === null || value === undefined || isNaN(value)) {
      return '-';
    }

    const absValue = Math.abs(value);
    const integerPart = Math.floor(absValue).toString();

    // If integer part has 4 or more digits, show only integer part (truncated to 4 digits)
    if (integerPart.length >= 4) {
      const truncatedInteger = integerPart.substring(0, 4);
      return (value < 0 ? '-' : '') + truncatedInteger;
    }

    // If integer part has less than 4 digits, show with appropriate decimal places (max 2)
    const remainingDigits = Math.min(4 - integerPart.length, 2); // Max 2 decimal places

    if (remainingDigits <= 0) {
      // No room for decimals
      return (value < 0 ? '-' : '') + integerPart;
    }

    // Round to the appropriate number of decimal places
    const multiplier = Math.pow(10, remainingDigits);
    const roundedValue = Math.round(absValue * multiplier) / multiplier;

    // Format with the exact number of decimal places needed
    let result = roundedValue.toFixed(remainingDigits);

    // Remove trailing zeros after decimal point
    if (result.includes('.')) {
      result = result.replace(/\.?0+$/, '');
    }

    return (value < 0 ? '-' : '') + result;
  }

  positionGoalDateTooltip() {
    if (!this.hintIconWrapperRef || !this.goalDateTooltipRef) {
      // Retry a few times if the tooltip is not yet available
      if (this.tooltipPositionRetryCount < this.tooltipPositionMaxRetries) {
        this.tooltipPositionRetryCount++;
        setTimeout(() => this.positionGoalDateTooltip(), 20);
      }
      return;
    }
    const iconRect = this.hintIconWrapperRef.nativeElement.getBoundingClientRect();
    const tooltipEl = this.goalDateTooltipRef.nativeElement as HTMLElement;
    const tooltipWidth = tooltipEl.offsetWidth;
    const tooltipHeight = tooltipEl.offsetHeight;
    const gap = 8;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const isMobile = viewportWidth <= 640;
    const isSmallMobile = viewportWidth <= 480;
    const isTablet = viewportWidth <= 768 && viewportWidth > 640;

    let top: number;
    let left: number;

    if (isSmallMobile) {
      // On small mobile, force center positioning with strict viewport bounds
      top = iconRect.bottom + gap;
      left = Math.max(8, (viewportWidth - tooltipWidth) / 2);

      // If tooltip is too wide for viewport, force it to fit
      if (tooltipWidth > viewportWidth - 16) {
        left = 8;
        tooltipEl.style.width = `${viewportWidth - 16}px`;
        tooltipEl.style.maxWidth = `${viewportWidth - 16}px`;
      }

      // Ensure tooltip doesn't go below viewport
      if (top + tooltipHeight > viewportHeight - 8) {
        top = Math.max(8, iconRect.top - tooltipHeight - gap);
      }

      // Final bounds check
      top = Math.max(8, Math.min(top, viewportHeight - tooltipHeight - 8));
      left = Math.max(8, Math.min(left, viewportWidth - tooltipWidth - 8));

    } else if (isMobile) {
      // On mobile (640px and below), smart positioning
      top = iconRect.bottom + gap;
      left = (viewportWidth - tooltipWidth) / 2;

      // If tooltip overflows horizontally, adjust
      if (left < 8) {
        left = 8;
      } else if (left + tooltipWidth > viewportWidth - 8) {
        left = viewportWidth - tooltipWidth - 8;
      }

      // If tooltip overflows vertically, position above
      if (top + tooltipHeight > viewportHeight - 8) {
        top = Math.max(8, iconRect.top - tooltipHeight - gap);
      }

      // Ensure tooltip stays within bounds
      top = Math.max(8, Math.min(top, viewportHeight - tooltipHeight - 8));
      left = Math.max(8, Math.min(left, viewportWidth - tooltipWidth - 8));

    } else if (isTablet) {
      // On tablet, position to the left but with better spacing
      top = iconRect.top + (iconRect.height / 2) - (tooltipHeight / 2);
      left = iconRect.left - tooltipWidth - gap;

      // If tooltip overflows left, position it to the right
      if (left < 8) {
        left = iconRect.right + gap;

        // If it still overflows on the right, center it
        if (left + tooltipWidth > viewportWidth - 8) {
          left = (viewportWidth - tooltipWidth) / 2;
          top = iconRect.bottom + gap;
        }
      }

      // Adjust vertical position if needed
      if (top < 8) top = 8;
      if (top + tooltipHeight > viewportHeight - 8) {
        top = viewportHeight - tooltipHeight - 8;
        if (top < 8) top = 8;
      }
    } else {
      // Desktop positioning (original logic)
      top = iconRect.top + (iconRect.height / 2) - (tooltipHeight / 2);
      left = iconRect.left - tooltipWidth - gap;

      // If tooltip overflows left, align to left edge
      if (left < 8) {
        left = 8;
      }

      // If tooltip overflows top, align to top edge
      if (top < 8) {
        top = 8;
      }

      // If tooltip overflows bottom, keep it within viewport
      if (top + tooltipHeight > viewportHeight - 8) {
        top = viewportHeight - tooltipHeight - 8;
        if (top < 8) top = 8;
      }
    }

    // Final safety check - ensure tooltip is always visible
    if (tooltipWidth > viewportWidth - 16) {
      tooltipEl.style.width = `${viewportWidth - 16}px`;
      tooltipEl.style.maxWidth = `${viewportWidth - 16}px`;
      left = 8;
    }

    tooltipEl.style.left = `${left}px`;
    tooltipEl.style.top = `${top}px`;
    this.tooltipPositioned = true;
  }

  // Notes Management Methods
  onAddNoteClick() {
    this.showAddNoteForm.set(true);
  }

  addNewNote() {
    const noteText = this.newNoteText().trim();
    if ((!noteText && this.selectedFiles.length === 0) || !this.currentProjectId) return;

    this.addNoteLoading.set(true);

    // If files are selected, send as FormData
    if (this.selectedFiles.length > 0) {
      const formData = new FormData();
      
      // Append text fields
      formData.append('textHTML', noteText);
      formData.append('objCode', 'project');
      formData.append('objID', this.currentProjectId!);
      formData.append('projectID', this.currentProjectId!);
      
      // Append files to FormData
      this.selectedFiles.forEach((file, index) => {
        formData.append('file', file, file.name);
      });

      this.noteService.createNote(formData).subscribe({
        next: (response: any) => {
          this.addNoteLoading.set(false);
          if (response && response.success && response.data && response.data.note) {
            const transformedNote = this.transformApiNoteToComponent(response.data.note);
            this.notesList.update(notes => [...notes, transformedNote]);
            this.toastrService.success(response.message || 'Note added successfully');
          }
          this.resetNoteForm();
        },
        error: (error) => {
          this.addNoteLoading.set(false);
          console.error('Error creating note:', error);
          this.toastrService.error(error.message || 'Failed to add note', 'Error');
        }
      });
    } else {
      // Regular text note without files
      const noteData = {
        textHTML: noteText,
        objCode: 'project',
        objID: this.currentProjectId!,
        projectID: this.currentProjectId!,
      };

      this.noteService.createNote(noteData).subscribe({
        next: (response: any) => {
          this.addNoteLoading.set(false);
          if (response && response.success && response.data && response.data.note) {
            const transformedNote = this.transformApiNoteToComponent(response.data.note);
            this.notesList.update(notes => [...notes, transformedNote]);
            this.toastrService.success(response.message || 'Note added successfully');
          }
          this.resetNoteForm();
        },
        error: (error) => {
          this.addNoteLoading.set(false);
          console.error('Error creating note:', error);
          this.toastrService.error(error.message || 'Failed to add note', 'Error');
        }
      });
    }
  }

  private resetNoteForm() {
    this.newNoteText.set('');
    this.selectedFiles = [];
    this.showAddNoteForm.set(false);
  }

  cancelAddNote() {
    this.showAddNoteForm.set(false);
    this.newNoteText.set('');
    this.selectedFiles = [];
  }

  // File upload methods
  onDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  onFileDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.handleFiles(Array.from(files));
    }
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.handleFiles(Array.from(input.files));
    }
  }

  private handleFiles(files: File[]) {
    const validFiles: File[] = [];
    const errors: string[] = [];

    files.forEach(file => {
      // Check file size
      if (file.size > this.maxFileSize) {
        errors.push(`${file.name}: File size exceeds 10MB limit`);
        return;
      }

      // Check file type
      if (!this.allowedFileTypes.includes(file.type)) {
        errors.push(`${file.name}: File type not supported`);
        return;
      }

      validFiles.push(file);
    });

    // Add valid files to selected files
    this.selectedFiles = [...this.selectedFiles, ...validFiles];

    // Show errors if any
    if (errors.length > 0) {
      this.toastrService.error(errors.join('<br>'), 'File Upload Errors');
    }

    // Show success message for valid files
    if (validFiles.length > 0) {
      this.toastrService.success(`${validFiles.length} file(s) added successfully`);
    }
  }

  removeFile(index: number) {
    this.selectedFiles.splice(index, 1);
  }

  clearSelectedFiles() {
    this.selectedFiles = [];
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  getFileIcon(fileType: string): string {
    if (fileType.startsWith('image/')) {
      return 'wl_icon_img-icon';
    } else if (fileType.startsWith('video/')) {
      return 'wl_icon_doc-video';
    } else if (fileType.startsWith('audio/')) {
      return 'wl_icon_doc-video';
    } else if (fileType.includes('pdf')) {
      return 'wl_icon_doc-pdf';
    } else if (fileType.includes('word') || fileType.includes('document')) {
      return 'wl_icon_doc-doc';
    } else if (fileType.includes('excel') || fileType.includes('spreadsheet')) {
      return 'wl_icon_doc-xls';
    } else if (fileType.includes('powerpoint') || fileType.includes('presentation')) {
      return 'wl_icon_doc-pptx';
    } else {
      return 'wl_icon_doc-file';
    }
  }

  togglePin(noteId: string) {
    this.notesList.update(notes =>
      notes.map(note =>
        note.id === noteId ? { ...note, isPinned: !note.isPinned } : note
      )
    );
  }

  deleteNote(noteId: string) {
    // Find the note to delete and set it for confirmation
    const note = this.notesList().find(n => n.id === noteId);
    if (note) {
      this.noteToDelete.set(note);
      this.showDeleteConfirmPopup.set(true);
    }
  }

  onDeleteConfirm(): void {
    const note = this.noteToDelete();
    if (note) {
      this.deleteNoteLoading.set(true);
      this.noteService.deleteNote(note.id).subscribe({
        next: (response: any) => {
          if (response && response.success) {
            // Remove the note from the list
            this.notesList.update(notes => notes.filter(n => n.id !== note.id));
            this.toastrService.success(response.message || 'Note deleted successfully');
          } else {
            this.toastrService.error('Failed to delete note', 'Error');
          }
          // Close popup and reset loading state
          this.deleteNoteLoading.set(false);
          this.showDeleteConfirmPopup.set(false);
          this.noteToDelete.set(null);
        },
        error: (error) => {
          console.error('Error deleting note:', error);
          this.toastrService.error(error.message || 'Failed to delete note', 'Error');
          // Close popup and reset loading state
          this.deleteNoteLoading.set(false);
          this.showDeleteConfirmPopup.set(false);
          this.noteToDelete.set(null);
        }
      });
    }
  }

  onDeleteCancel(): void {
    this.showDeleteConfirmPopup.set(false);
    this.noteToDelete.set(null);
  }

  startEditNote(noteId: string) {
    this.editingNoteId.set(noteId);
  }

  saveEditNote(noteId: string, newText: string) {
    if (!newText.trim()) return;

    this.editNoteLoading.set(true);

    this.notesList.update(notes =>
      notes.map(note =>
        note.id === noteId ? { ...note, text: newText.trim() } : note
      )
    );
    this.editingNoteId.set(null);

    // Simulate API call delay and then reset loading
    setTimeout(() => {
      this.editNoteLoading.set(false);
    }, 1000);
  }

  cancelEditNote() {
    this.editingNoteId.set(null);
  }

  isEditingNote(noteId: string): boolean {
    return this.editingNoteId() === noteId;
  }

  getCardColorClass(index: number): string {
    const colors = ['peach', 'blue', 'white'];
    return colors[index % 3];
  }

  getCardBackgroundColor(index: number): string {
    const isDarkMode = document.documentElement.getAttribute('data-theme') === 'dark' ||
                       window.matchMedia('(prefers-color-scheme: dark)').matches;

    // Light mode: More visible colors with noticeable tints, avoiding very light colors
    const lightColors = ['#ffe8d6', '#e0e7ff', '#f1f5f9'];
    // Dark mode: More visible backgrounds with higher opacity
    const darkColors = ['rgba(251, 191, 165, 0.3)', 'rgba(148, 156, 254, 0.3)', 'rgba(203, 213, 225, 0.25)'];

    const colors = isDarkMode ? darkColors : lightColors;
    return colors[index % 3];
  }

  getCardBorderColor(index: number): string {
    const isDarkMode = document.documentElement.getAttribute('data-theme') === 'dark' ||
                       window.matchMedia('(prefers-color-scheme: dark)').matches;

    // Light mode: More visible borders with higher opacity and darker colors
    const lightBorders = ['rgba(251, 191, 165, 0.7)', 'rgba(148, 156, 254, 0.7)', '#9ca3af'];
    // Dark mode: Much more visible borders with high opacity
    const darkBorders = ['rgba(251, 191, 165, 0.7)', 'rgba(148, 156, 254, 0.7)', 'rgba(203, 213, 225, 0.7)'];

    const borders = isDarkMode ? darkBorders : lightBorders;
    return borders[index % 3];
  }

  getPinnedOverlayColor(index: number): string {
    const isDarkMode = document.documentElement.getAttribute('data-theme') === 'dark' ||
                       window.matchMedia('(prefers-color-scheme: dark)').matches;

    const colorIndex = index % 3;

    if (isDarkMode) {
      const darkOverlays = [
        'linear-gradient(135deg, rgba(251, 191, 165, 0.2) 0%, rgba(251, 191, 165, 0.12) 100%)',
        'linear-gradient(135deg, rgba(148, 156, 254, 0.2) 0%, rgba(117, 126, 233, 0.12) 100%)',
        'linear-gradient(135deg, rgba(148, 156, 254, 0.18) 0%, rgba(117, 126, 233, 0.1) 100%)'
      ];
      return darkOverlays[colorIndex];
    } else {
      const lightOverlays = [
        'linear-gradient(135deg, rgba(251, 191, 165, 0.15) 0%, rgba(251, 191, 165, 0.08) 100%)',
        'linear-gradient(135deg, rgba(148, 156, 254, 0.15) 0%, rgba(117, 126, 233, 0.08) 100%)',
        'linear-gradient(135deg, rgba(148, 156, 254, 0.12) 0%, rgba(117, 126, 233, 0.06) 100%)'
      ];
      return lightOverlays[colorIndex];
    }
  }

  // Helper methods to get documents by type
  getImageDocuments(note: Note): NoteDocument[] {
    if (!note.documents || note.documents.length === 0) {
      // Fallback to old structure
      if (note.fileUrl && note.fileType && (note.fileType.startsWith('image/') || this.isImageFile(note.fileType, note.fileName))) {
        return [{
          name: note.fileName || 'Image',
          type: note.fileType,
          url: note.fileUrl
        }];
      }
      return [];
    }
    return note.documents.filter(doc => 
      doc.type.startsWith('image/') || this.isImageFile(doc.type, doc.name)
    );
  }

  getVideoDocuments(note: Note): NoteDocument[] {
    if (!note.documents || note.documents.length === 0) {
      // Fallback to old structure
      if (note.fileUrl && note.fileType && note.fileType.startsWith('video/')) {
        return [{
          name: note.fileName || 'Video',
          type: note.fileType,
          url: note.fileUrl
        }];
      }
      return [];
    }
    return note.documents.filter(doc => doc.type.startsWith('video/'));
  }

  getAudioDocuments(note: Note): NoteDocument[] {
    if (!note.documents || note.documents.length === 0) {
      // Fallback to old structure
      if (note.fileUrl && note.fileType && note.fileType.startsWith('audio/')) {
        return [{
          name: note.fileName || 'Audio',
          type: note.fileType,
          url: note.fileUrl
        }];
      }
      return [];
    }
    return note.documents.filter(doc => doc.type.startsWith('audio/'));
  }

  getDocumentFiles(note: Note): NoteDocument[] {
    if (!note.documents || note.documents.length === 0) {
      // Fallback to old structure
      if (note.fileUrl && note.fileType && 
          !note.fileType.startsWith('image/') && 
          !note.fileType.startsWith('video/') && 
          !note.fileType.startsWith('audio/')) {
        return [{
          name: note.fileName || 'Document',
          type: note.fileType,
          url: note.fileUrl
        }];
      }
      return [];
    }
    return note.documents.filter(doc => 
      !doc.type.startsWith('image/') && 
      !doc.type.startsWith('video/') && 
      !doc.type.startsWith('audio/')
    );
  }

  // File preview methods
  openFilePreview(note: any, doc?: NoteDocument) {
    // If a specific document is provided, use it
    const docToPreview = doc || (note.documents && note.documents.length > 0 ? note.documents[0] : null);
    
    let fileUrl: string | undefined;
    let fileName: string | undefined;
    let fileType: string | undefined;

    if (docToPreview) {
      fileUrl = docToPreview.url;
      fileName = docToPreview.name;
      fileType = docToPreview.type;
    } else if (note.fileUrl) {
      // Fallback to old structure
      fileUrl = note.fileUrl;
      fileName = note.fileName;
      fileType = note.fileType;
    }

    if (!fileUrl) {
      return;
    }

    // If note type is text, don't show preview
    if (note.type === 'text' && !docToPreview) {
      return;
    }

    // Determine file type - use document type if available, otherwise detect from fileType/fileName
    let previewType = note.type;
    if (fileType) {
      if (fileType.startsWith('audio/')) {
        previewType = 'audio';
      } else if (fileType.startsWith('video/')) {
        previewType = 'video';
      } else if (fileType.startsWith('image/') || this.isImageFile(fileType, fileName || fileUrl)) {
        previewType = 'image';
      } else {
        previewType = 'document';
      }
    } else if (!previewType || previewType === 'text') {
      // Fallback: detect type from fileType or fileName
      if (this.isImageFile(fileType, fileName || fileUrl)) {
        previewType = 'image';
      } else if (fileType?.startsWith('video/')) {
        previewType = 'video';
      } else if (fileType?.startsWith('audio/')) {
        previewType = 'audio';
      } else {
        previewType = 'document';
      }
    }

    if (previewType === 'document') {
      window.open(fileUrl, '_blank', 'noopener,noreferrer');
      return;
    }

    this.currentFilePreview.set({
      noteId: note.id,
      fileName: fileName || 'Untitled',
      fileUrl: fileUrl,
      fileType: fileType || '',
      type: previewType
    });
    this.showFilePreview.set(true);

    document.body.style.overflow = 'hidden';
  }

  closeFilePreview() {
    this.showFilePreview.set(false);
    document.body.style.overflow = '';

    setTimeout(() => {
      this.currentFilePreview.set(null);
    }, 300);
  }

  // Handle image thumbnail error (hide broken images)
  onImageThumbnailError(event: Event, noteId: string): void {
    const target = event.target as HTMLImageElement;
    if (target) {
      target.style.display = 'none';
    }
    // Remove from loaded set if it was there
    this.imageThumbnailLoaded.update(loaded => {
      const newSet = new Set(loaded);
      newSet.delete(noteId);
      return newSet;
    });
  }

  // Handle image thumbnail load success
  onImageThumbnailLoad(event: Event, noteId: string): void {
    const target = event.target as HTMLImageElement;
    if (target && target.complete && target.naturalWidth > 0) {
      this.imageThumbnailLoaded.update(loaded => {
        const newSet = new Set(loaded);
        newSet.add(noteId);
        return newSet;
      });
    }
  }

  // Check if image is already loaded (for cached images)
  checkImageLoaded(imageElement: HTMLImageElement, noteId: string): void {
    if (imageElement && imageElement.complete && imageElement.naturalWidth > 0) {
      this.imageThumbnailLoaded.update(loaded => {
        const newSet = new Set(loaded);
        newSet.add(noteId);
        return newSet;
      });
    }
  }

  // Check all image thumbnails that might already be loaded (cached)
  checkLoadedImages(): void {
    const imageNotes = this.notesList().filter(note => {
      const imageDocs = this.getImageDocuments(note);
      return imageDocs.length > 0 || (note.type === 'image' && note.fileUrl);
    });
    
    imageNotes.forEach(note => {
      const imageDocs = this.getImageDocuments(note);
      if (imageDocs.length > 0) {
        // Check each image document
        imageDocs.forEach((doc, index) => {
          const imageElement = document.querySelector(`img[data-note-id="${note.id}_${index}"]`) as HTMLImageElement;
          if (imageElement) {
            this.checkImageLoaded(imageElement, `${note.id}_${index}`);
          }
        });
      } else if (note.fileUrl) {
        // Fallback to old structure
        const imageElement = document.querySelector(`img[data-note-id="${note.id}"]`) as HTMLImageElement;
        if (imageElement) {
          this.checkImageLoaded(imageElement, note.id);
        }
      }
    });
  }

  // Check if image thumbnail is loaded for a note
  isImageThumbnailLoaded(noteId: string): boolean {
    return this.imageThumbnailLoaded().has(noteId);
  }

  // Check if note has text content (not empty or just whitespace/HTML tags)
  hasTextContent(text: string | undefined | null): boolean {
    if (!text) return false;
    // Remove HTML tags and whitespace to check if there's actual content
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = text;
    const textContent = tempDiv.textContent || tempDiv.innerText || '';
    return textContent.trim().length > 0;
  }

  /**
   * Check if a file is an image based on MIME type or file extension
   * Supports common web image formats including webp
   */
  private isImageFile(fileType?: string, fileName?: string): boolean {
    if (!fileType && !fileName) return false;

    // Common image MIME types including webp
    const imageMimeTypes = [
      'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 
      'image/webp', 'image/svg+xml', 'image/bmp', 'image/tiff',
      'image/x-icon', 'image/vnd.microsoft.icon'
    ];

    // Common image file extensions including webp
    const imageExtensions = [
      '.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg', 
      '.bmp', '.tiff', '.tif', '.ico', '.jfif'
    ];

    // Check MIME type
    if (fileType) {
      const normalizedMimeType = fileType.toLowerCase();
      if (imageMimeTypes.some(mime => normalizedMimeType === mime || normalizedMimeType.startsWith('image/'))) {
        return true;
      }
    }

    // Check file extension as fallback
    if (fileName) {
      const normalizedFileName = fileName.toLowerCase();
      return imageExtensions.some(ext => normalizedFileName.endsWith(ext));
    }

    return false;
  }

  private transformApiNoteToComponent(apiNote: ApiNoteResponse): Note {
    // Determine note type based on document array, fileType, or content
    let type: 'text' | 'image' | 'video' | 'audio' | 'document' = 'text';
    let fileUrl: string | undefined;
    let fileName: string | undefined;
    let fileType: string | undefined;
    let documents: NoteDocument[] | undefined;

    // Check if note has document array (new API structure)
    if (apiNote.document && apiNote.document.length > 0) {
      // Store all documents
      documents = apiNote.document.map(doc => ({
        name: doc.name,
        type: doc.type,
        url: doc.url
      }));

      // Use the first document for backward compatibility (fileUrl, fileName, fileType)
      const firstDoc = apiNote.document[0];
      fileUrl = firstDoc.url;
      fileName = firstDoc.name;
      fileType = firstDoc.type;

      // Determine type from first document type (for backward compatibility)
      if (firstDoc.type.startsWith('audio/')) {
        type = 'audio';
      } else if (firstDoc.type.startsWith('video/')) {
        type = 'video';
      } else if (firstDoc.type.startsWith('image/')) {
        type = 'image';
      } else if (this.isImageFile(firstDoc.type, firstDoc.name)) {
        type = 'image';
      } else {
        type = 'document';
      }
    } else if (apiNote.fileType || apiNote.url) {
      // Fallback to old API structure (url and fileType)
      fileUrl = apiNote.url;
      fileType = apiNote.fileType;
      fileName = apiNote.url ? apiNote.url.split('\\').pop() || apiNote.url.split('/').pop() : undefined;

      // Convert old structure to documents array for consistency
      if (fileUrl && fileName && fileType) {
        documents = [{
          name: fileName,
          type: fileType,
          url: fileUrl
        }];
      }

      if (apiNote.fileType) {
        if (apiNote.fileType.startsWith('audio/')) {
          type = 'audio';
        } else if (apiNote.fileType.startsWith('video/')) {
          type = 'video';
        } else if (apiNote.fileType.startsWith('image/')) {
          // Standard image MIME type check (fast path)
          type = 'image';
        } else if (this.isImageFile(apiNote.fileType, apiNote.url)) {
          // Fallback: check for image types that might not have 'image/' prefix
          type = 'image';
        } else {
          type = 'document';
        }
      } else if (apiNote.url) {
        // Fallback: check file extension if fileType is not available
        const fileNameFromUrl = apiNote.url.split('\\').pop() || apiNote.url.split('/').pop() || '';
        if (this.isImageFile(undefined, fileNameFromUrl)) {
          type = 'image';
        }
      }
    }

    // Format date
    const date = new Date(apiNote.createdAt);
    const formattedDate = date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });

    return {
      id: apiNote._id,
      text: apiNote.textHTML,
      type: type,
      user: apiNote.createdBy.name,
      date: formattedDate,
      createdAt: apiNote.createdAt, // Include raw timestamp for sorting
      isPinned: false, // API doesn't seem to have pin status, defaulting to false
      fileUrl: fileUrl, // Keep for backward compatibility
      fileName: fileName, // Keep for backward compatibility
      fileType: fileType, // Keep for backward compatibility
      documents: documents, // New: array of all documents
      time: type === 'audio' || type === 'video' ? '00:00' : undefined
    };
  }

  /**
   * Load notes from API when notes tab is selected
   */
  loadNotes(): void {
    if (!this.currentProjectId) {
      console.warn('No current project ID available for loading notes');
      this.notesLoading.set(false);
      return;
    }

    this.notesLoading.set(true);
    this.notesError.set(null);

    this.noteService.getNotes(this.currentProjectId, 'project').subscribe({
      next: (response: ApiNotesResponse) => {
        if (response && response.success && response.data) {
          // Transform API response to component format
          const transformedNotes = response.data.map(apiNote => this.transformApiNoteToComponent(apiNote));
          this.notesList.set(transformedNotes);
          
          // Check for already-loaded images after view updates
          setTimeout(() => {
            this.checkLoadedImages();
          }, 100);
        } else {
          this.notesList.set([]);
        }
        this.notesLoading.set(false);
      },
      error: (error) => {
        console.error('Error loading notes:', error);
        this.notesError.set('Failed to load notes');
        this.notesLoading.set(false);
        this.toastrService.error('Failed to load notes', 'Error');
      }
    });
  }

  downloadFile(event: Event) {
    event.stopPropagation();
    const preview = this.currentFilePreview();
    if (!preview) return;

    const link = document.createElement('a');
    link.href = preview.fileUrl;
    link.download = preview.fileName;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  getDocumentIcon(fileType: string): string {
    if (fileType.includes('pdf')) {
      return 'wl_icon_doc-pdf';
    } else if (fileType.includes('word') || fileType.includes('document')) {
      return 'wl_icon_doc-doc';
    } else if (fileType.includes('excel') || fileType.includes('spreadsheet')) {
      return 'wl_icon_doc-xls';
    } else if (fileType.includes('powerpoint') || fileType.includes('presentation')) {
      return 'wl_icon_doc-pptx';
    } else {
      return 'wl_icon_doc-export';
    }
  }


   shouldHideCompletionDateEdit(): boolean {
    const currentProjectData = this.projectSerivce.getCurrentProjectData();
    if (!currentProjectData) {
      return false;
    }

    // Check if project status is "complete"
    const projectStatus = currentProjectData.projectStatusData?.statusName?.toLowerCase();
    const isStatusComplete = projectStatus === 'complete';

    // Check if project ID matches the specified value
    const isSpecificProject = this.currentProjectId === '93818296-80c8-415f-a1ce-1d1853c1d31a';

    return isStatusComplete || isSpecificProject;
  }
}


